import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, Apple, MessageCircle, FileText, BookOpen, LogOut, User, TrendingUp, Droplet, Moon, Heart, Wind } from 'lucide-react';
import { Button } from '../ui/button';
import NutritionAnalyzer from './NutritionAnalyzer';
import HealthLogger from './HealthLogger';
import ZenBot from './ZenBot';
import Reports from './Reports';
import Journal from './Journal';

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [healthStats, setHealthStats] = useState({
    steps: 0,
    water: 0,
    sleep: 0,
    calories: 0
  });

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('shansozen_token');
    const userData = localStorage.getItem('shansozen_user');

    if (!token || !userData) {
      navigate('/shansozen/login');
      return;
    }

    setUser(JSON.parse(userData));
    fetchHealthStats();
  }, [navigate]);

  const fetchHealthStats = async () => {
    try {
      const token = localStorage.getItem('shansozen_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shansozen/health-stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setHealthStats(data);
      }
    } catch (error) {
      console.error('Error fetching health stats:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('shansozen_token');
    localStorage.removeItem('shansozen_user');
    navigate('/shansozen/login');
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black">
      {/* Header */}
      <header className="bg-gray-800/50 backdrop-blur-sm border-b border-gray-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-purple-500 rounded-xl flex items-center justify-center">
                <span className="text-2xl">🧘</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">ShansoZen</h1>
                <p className="text-xs text-gray-400">Holistic Health Monitor</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-semibold text-white">{user.name || user.email}</p>
                <p className="text-xs text-gray-400">Premium Member</p>
              </div>
              <Button
                onClick={handleLogout}
                className="bg-gray-700 hover:bg-gray-600"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {[
            { id: 'overview', label: 'Overview', icon: TrendingUp },
            { id: 'nutrition', label: 'Nutrition', icon: Apple },
            { id: 'logger', label: 'Log Health', icon: Activity },
            { id: 'zenbot', label: 'ZenBot', icon: MessageCircle },
            { id: 'reports', label: 'Reports', icon: FileText },
            { id: 'journal', label: 'Journal', icon: BookOpen }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-cyan-500 to-purple-500 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-white mb-6">Today's Overview</h2>
              
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 border border-blue-500/30 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-3">
                    <Activity className="w-8 h-8 text-blue-400" />
                    <span className="text-xs text-blue-400 font-semibold">STEPS</span>
                  </div>
                  <p className="text-3xl font-bold text-white mb-1">{healthStats.steps}</p>
                  <p className="text-sm text-gray-400">Goal: 10,000</p>
                </div>

                <div className="bg-gradient-to-br from-cyan-500/20 to-cyan-600/20 border border-cyan-500/30 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-3">
                    <Droplet className="w-8 h-8 text-cyan-400" />
                    <span className="text-xs text-cyan-400 font-semibold">WATER</span>
                  </div>
                  <p className="text-3xl font-bold text-white mb-1">{healthStats.water}L</p>
                  <p className="text-sm text-gray-400">Goal: 2.5L</p>
                </div>

                <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 border border-purple-500/30 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-3">
                    <Moon className="w-8 h-8 text-purple-400" />
                    <span className="text-xs text-purple-400 font-semibold">SLEEP</span>
                  </div>
                  <p className="text-3xl font-bold text-white mb-1">{healthStats.sleep}h</p>
                  <p className="text-sm text-gray-400">Goal: 8h</p>
                </div>

                <div className="bg-gradient-to-br from-green-500/20 to-green-600/20 border border-green-500/30 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-3">
                    <Apple className="w-8 h-8 text-green-400" />
                    <span className="text-xs text-green-400 font-semibold">CALORIES</span>
                  </div>
                  <p className="text-3xl font-bold text-white mb-1">{healthStats.calories}</p>
                  <p className="text-sm text-gray-400">Today</p>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
                <h3 className="text-xl font-bold text-white mb-4">Quick Actions</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { label: 'Analyze Meal', tab: 'nutrition', icon: Apple, color: 'cyan' },
                    { label: 'Log Health', tab: 'logger', icon: Activity, color: 'blue' },
                    { label: 'Ask ZenBot', tab: 'zenbot', icon: MessageCircle, color: 'purple' },
                    { label: 'View Reports', tab: 'reports', icon: FileText, color: 'green' },
                    { label: 'Write Journal', tab: 'journal', icon: BookOpen, color: 'yellow' }
                  ].map((action) => {
                    const Icon = action.icon;
                    return (
                      <button
                        key={action.tab}
                        onClick={() => setActiveTab(action.tab)}
                        className={`flex flex-col items-center gap-2 p-4 bg-${action.color}-500/10 border border-${action.color}-500/30 rounded-lg hover:bg-${action.color}-500/20 transition-all`}
                      >
                        <Icon className={`w-6 h-6 text-${action.color}-400`} />
                        <span className="text-sm font-semibold text-white">{action.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'nutrition' && <NutritionAnalyzer />}
          {activeTab === 'logger' && <HealthLogger onUpdate={fetchHealthStats} />}
          {activeTab === 'zenbot' && <ZenBot />}
          {activeTab === 'reports' && <Reports />}
          {activeTab === 'journal' && <Journal />}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
